import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { FaCalendarAlt, FaCheck, FaTimes, FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "../context/LanguageContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import "../styles/common.css";
import "./ModifierEvenement.css";

export default function ModifierEvenement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    animal: "",
    type_evenement: "",
    date: "",
    details: "",
  });

  const [animaux, setAnimaux] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    animal: "",
    type_evenement: "",
    date: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    animal: false,
    type_evenement: false,
    date: false,
  });

  // Ref to track navigation timers for cleanup
  const navigationTimersRef = useRef([]);
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Types d'événements à partir du modèle Django
  // Note: "naissance" et "achat" sont créés automatiquement lors de l'ajout d'un animal
  const TYPE_EVENEMENT_CHOICES = useMemo(() => [
    { value: "naissance", label: t.birth },
    { value: "vendu", label: t.sale },
    { value: "mort", label: t.death },
    { value: "achat", label: t.purchase },
    { value: "zakat", label: t.zakat },
  ], [t]);

  useEffect(() => {
    let isMounted = true;
    let navigationTimer = null;
    
    const fetchEvenement = async () => {
      try {
        const response = await api.get(`/api/evenements/${id}/`);
        const eventData = response.data;
        
        // Si l'événement est "mort" ou "vendu", rediriger vers la liste avec un message
        if (eventData.type_evenement === "mort" || eventData.type_evenement === "vendu") {
          toast.error("Les événements de décès et de vente ne peuvent pas être modifiés. Vous pouvez les annuler depuis la liste des événements.");
          navigationTimer = setTimeout(() => {
            if (isMounted) {
              navigate("/evenements");
            }
          }, 2000);
          return;
        }
        
        if (isMounted) {
          setFormData(eventData);
          setInitialData(eventData);
          setLoading(false);
        }
      } catch (error) {
        console.error("Erreur lors du chargement de l'événement:", error);
        if (isMounted) {
          toast.error("Erreur lors du chargement de l'événement.");
          setLoading(false);
        }
      }
    };
    
    fetchEvenement();
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (navigationTimer) {
        clearTimeout(navigationTimer);
      }
    };
  }, [id, navigate]);

  useEffect(() => {
    const fetchAnimaux = async () => {
      try {
        const animauxRes = await api.get("/api/animaux/");
        setAnimaux(animauxRes.data);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      }
    };
    fetchAnimaux();
  }, []);

  // Cleanup all navigation timers and set mounted flag on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      navigationTimersRef.current.forEach(timer => {
        clearTimeout(timer);
      });
      navigationTimersRef.current = [];
    };
  }, []);

  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "animal" || name === "type_evenement" || name === "date") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const hasChanges = () => {
    if (!initialData) return false;
    
    // Normaliser les valeurs pour la comparaison (null/undefined -> "")
    const normalize = (val) => (val === null || val === undefined) ? "" : String(val).trim();
    
    return (
      String(formData.animal) !== String(initialData.animal || "") ||
      normalize(formData.type_evenement) !== normalize(initialData.type_evenement) ||
      normalize(formData.date) !== normalize(initialData.date) ||
      normalize(formData.details) !== normalize(initialData.details)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      animal: true,
      type_evenement: true,
      date: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      animal: validateField("animal", formData.animal),
      type_evenement: validateField("type_evenement", formData.type_evenement),
      date: validateField("date", formData.date),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setLoading(true);
    try {
      const previousType = initialData?.type_evenement;
      const newType = formData.type_evenement;
      
      // Mettre à jour l'événement
      await api.put(`/api/evenements/${id}/`, formData);
      
      // Si le type d'événement change vers "vendu" ou "mort", ou si on modifie un événement "vendu"/"mort"
      if ((newType === "vendu" || newType === "mort") && formData.animal) {
        try {
          const animalData = {
            statut: newType === "vendu" ? "vendu" : "mort"
          };
          await api.patch(`/api/animaux/${formData.animal}/`, animalData);
        } catch (animalError) {
          console.error("Erreur lors de la mise à jour du statut de l'animal:", animalError);
          // Ne pas bloquer la modification de l'événement si la mise à jour du statut échoue
          toast.warning("Événement modifié mais erreur lors de la mise à jour du statut de l'animal.");
        }
      } else if ((previousType === "vendu" || previousType === "mort") && 
                 newType !== "vendu" && newType !== "mort" && formData.animal) {
        // Si on change un événement "vendu" ou "mort" vers un autre type, remettre l'animal à "actif"
        try {
          const animalData = { statut: "actif" };
          await api.patch(`/api/animaux/${formData.animal}/`, animalData);
        } catch (animalError) {
          console.error("Erreur lors de la mise à jour du statut de l'animal:", animalError);
          toast.warning("Événement modifié mais erreur lors de la mise à jour du statut de l'animal.");
        }
      }
      
      toast.success("Événement modifié avec succès !");
      const navigationTimer = setTimeout(() => {
        if (isMountedRef.current) {
          navigate("/evenements");
        }
      }, 500);
      navigationTimersRef.current.push(navigationTimer);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la mise à jour.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const eventType = formData.type_evenement;
      const animalId = formData.animal;
      
      // Supprimer l'événement
      await api.delete(`/api/evenements/${id}/`);
      
      // Si l'événement supprimé était "vendu" ou "mort", remettre l'animal à "actif"
      if ((eventType === "vendu" || eventType === "mort") && animalId) {
        try {
          const animalData = { statut: "actif" };
          await api.patch(`/api/animaux/${animalId}/`, animalData);
        } catch (animalError) {
          console.error("Erreur lors de la mise à jour du statut de l'animal:", animalError);
          toast.warning("Événement supprimé mais erreur lors de la mise à jour du statut de l'animal.");
        }
      }
      
      setShowDeleteModal(false);
      toast.success("Événement supprimé avec succès !");
      const navigationTimer = setTimeout(() => {
        if (isMountedRef.current) {
          navigate("/evenements");
        }
      }, 500);
      navigationTimersRef.current.push(navigationTimer);
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la suppression.";
      toast.error(errorMessage);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };



  if (loading) {
    return (
      <div className="modifier-evenement-page">
        <div className="loading-container">
          <p className="loading-text">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-evenement-page">
      <div className="title-badge">
        <FaCalendarAlt className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER L'ÉVÉNEMENT</span>
      </div>

      <form onSubmit={handleSubmit} className="modifier-evenement-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="animal">
              Animal <span className="required-indicator">*</span>
            </label>
            <select
              id="animal"
              name="animal"
              value={formData.animal || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.animal && touchedFields.animal ? "input-error" : ""}
            >
              <option value="">{t.selectAnimal}</option>
              {animaux.map((animal) => (
                <option key={animal.id} value={animal.id}>
                  {animal.matricule}
                </option>
              ))}
            </select>
            {fieldErrors.animal && touchedFields.animal && (
              <span className="field-error-message">{fieldErrors.animal}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="type_evenement">
              {t.eventTypeLabel} <span className="required-indicator">*</span>
            </label>
            <select
              id="type_evenement"
              name="type_evenement"
              value={formData.type_evenement || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.type_evenement && touchedFields.type_evenement ? "input-error" : ""}
            >
              <option value="">{t.selectType}</option>
              {TYPE_EVENEMENT_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            {fieldErrors.type_evenement && touchedFields.type_evenement && (
              <span className="field-error-message">{fieldErrors.type_evenement}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="date">
              Date de l'événement <span className="required-indicator">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date && touchedFields.date ? "input-error" : ""}
            />
            {fieldErrors.date && touchedFields.date && (
              <span className="field-error-message">{fieldErrors.date}</span>
            )}
          </div>

          <div className="form full-width">
            <label htmlFor="details">{t.detailsLabel}</label>
            <textarea
              id="details"
              name="details"
              value={formData.details || ""}
              onChange={handleChange}
              placeholder={t.eventDetails}
              rows="3"
            ></textarea>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!hasChanges() || loading}
          >
            <FaCheck />
            <span>{loading ? "Enregistrement..." : "Enregistrer"}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="delete-but"
            disabled={loading}
          >
            <FaTrash />
            <span>Supprimer</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/evenements")}
            className="cancel-but"
            disabled={loading}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      {/* Modal de suppression */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName=""
        itemType="événement"
        consequences={[]}
        requireConfirmation={false}
        isLoading={isDeleting}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
